import type { KeyboardEvent } from 'react';
import type {
  ResultsAiStatusResponse,
  ResultsAiThreadResponse,
  ResultsAiUsageResponse,
  ResultsAiView,
} from 'definitions';
import type { UserSummary } from 'pertentodb/types';
import type { ResultsTurn } from './results-ai-thread';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RESULTS_AI_CORRECTIONS, RESULTS_AI_GOALS, RESULTS_AI_METHODS } from 'definitions';
import { meterView } from 'helpers/ai-credits';
import { ALL_CURRENCIES } from 'helpers/currency';
import { useClient } from 'hooks/useClient';
import { useGlobal } from 'hooks/useGlobal';
import { AI_CREDIT_METER_KEY } from '../billing/useAiCreditMeter';
import { applyEvent, fromThreadTurn, mergeTurns, newTurn, senderName, workingPhase } from './results-ai-thread';
import { ResultsChatError, streamResultsChat } from './results-ai-stream';

export const RESULTS_AI_STATUS_KEY = ['RESULTS_AI_STATUS'];
export const RESULTS_AI_PANEL_KEY = 'RESULTS_AI_PANEL_OPEN';
export const RESULTS_AI_VIEW_KEY = 'RESULTS_AI_VIEW';
export const resultsAiThreadKey = (experimentId: number) => ['RESULTS_AI_THREAD', experimentId];
export const resultsAiLocalKey = (experimentId: number) => ['RESULTS_AI_LOCAL_TURNS', experimentId];
export const resultsAiUsageKey = (experimentId: number) => ['RESULTS_AI_USAGE', experimentId];

export const RESULTS_AI_INPUT_ID = 'results-ai-input';
export const RESULTS_AI_OUT_OF_CREDITS_ID = 'results-ai-out-of-credits';

/** Starting points for an empty conversation, sent as they read. */
/** A draft has no result yet: what is worth asking before it starts (decided 2026-10-20). */
export const DRAFT_SUGGESTED_QUESTIONS = [
  'Is this set up correctly?',
  'Is the hypothesis testable?',
  'How long will this need to run?',
] as const;

export const SUGGESTED_QUESTIONS = [
  'Is this result significant?',
  'Can I trust this result yet?',
  'What should I do next?',
] as const;

/** How often the thread is read again while a colleague's question is being answered. */
export const COLLEAGUE_POLL_MS = 10_000;
/** ...and while the person's own is still being finished (another tab, or saved after Stop). */
export const OWN_POLL_MS = 1_000;

const STOPPED_MESSAGE = 'Stopped. What was written so far is kept; ask again when you are ready.';

/** What the answers read, and its name in the panel's header ("Conversions · SEK"). */
export type ResultsAiViewState = { view: ResultsAiView; label: string };

/** The page's own defaults (useExperimentStats), until the Statistics tab says otherwise. */
export const DEFAULT_RESULTS_AI_VIEW: ResultsAiViewState = {
  view: { currency: ALL_CURRENCIES, goal: 'Conversions', method: 'frequentist', correction: 'holm-bonferroni' },
  label: 'Conversions · All currencies',
};

const resultsAiViewKey = (experimentId: number) => [RESULTS_AI_VIEW_KEY, String(experimentId)];

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | undefined =>
  values.find((candidate) => candidate === value);

/**
 * Before the Statistics tab has reported anything this visit: its choices as the URL holds them
 * (useExperimentStats keeps them in ?goal, ?currency, ?method and ?correction, and a reload on
 * another tab keeps them there), else the page's defaults. The server checks every value again.
 */
export const viewFromUrl = (search: string): ResultsAiViewState => {
  const params = new URLSearchParams(search);
  const fallback = DEFAULT_RESULTS_AI_VIEW.view;
  const view: ResultsAiView = {
    goal: oneOf(RESULTS_AI_GOALS, params.get('goal')) ?? fallback.goal,
    currency: params.get('currency')?.trim() || fallback.currency,
    method: oneOf(RESULTS_AI_METHODS, params.get('method')) ?? fallback.method,
    correction: oneOf(RESULTS_AI_CORRECTIONS, params.get('correction')) ?? fallback.correction,
  };
  const currency = view.currency === ALL_CURRENCIES ? 'All currencies' : view.currency;
  return { view, label: `${view.goal} · ${currency}` };
};

type ReportResultsAiViewParams = { experimentId: number } & ResultsAiViewState;

/**
 * The Statistics tab says which numbers are on screen, so a question asked there, or later from
 * another tab of the same experiment, reads the objective, currency, method and correction last
 * chosen. Syncing a shared value, not data fetching.
 */
export const useReportResultsAiView = ({ experimentId, view, label }: ReportResultsAiViewParams) => {
  const [, setShared] = useGlobal<ResultsAiViewState>(resultsAiViewKey(experimentId), DEFAULT_RESULTS_AI_VIEW);
  const signature = JSON.stringify([experimentId, view, label]);
  useEffect(() => {
    setShared({ view, label });
    // `signature` stands for the experiment, view and label: a new object each render must not
    // write each render.
  }, [signature]);
};

type UseResultsAiParams = { experimentId: number };

type Thread = ResultsAiThreadResponse<UserSummary>;

/**
 * The results chat (P-PER-44), on every tab of an experiment (decided 2026-10-20): the field sits
 * in the header, the panel docks beside the page. The conversation is the experiment's, saved and
 * shared: GET /thread is what everyone sees, and this browser's own turn sits beside it (in the
 * query cache, per experiment) until the server has stored it. One question runs per experiment at
 * a time; while a colleague's runs, the thread is read again every COLLEAGUE_POLL_MS. Owned by the
 * experiment screen, not the panel or a tab, so a turn keeps going while the panel is closed or the
 * tab changes; leaving the experiment stops it.
 */
export const useResultsAi = ({ experimentId }: UseResultsAiParams) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const localKey = resultsAiLocalKey(experimentId);
  const [{ view, label: viewLabel }] = useGlobal<ResultsAiViewState>(
    resultsAiViewKey(experimentId),
    viewFromUrl(window.location.search),
  );
  // Per experiment: open on one stays open across its tabs, and never opens itself on another.
  const [open, setOpen] = useGlobal<boolean>([RESULTS_AI_PANEL_KEY, String(experimentId)], false);
  const [draft, setDraft] = useState('');
  const [noticeError, setNoticeError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const status = useQuery<ResultsAiStatusResponse>({
    queryKey: RESULTS_AI_STATUS_KEY,
    queryFn: () => client.get('/results-ai/status'),
    staleTime: 5 * 60_000,
  });
  const available = status.data?.available ?? false;
  const acknowledged = status.data?.acknowledged ?? false;

  // Named, so the field can read both before it sends (submitFromField): they load with the panel.
  const usageQuery = {
    queryKey: resultsAiUsageKey(experimentId),
    queryFn: (): Promise<ResultsAiUsageResponse> => client.get(`/results-ai/usage?experimentId=${experimentId}`),
  };
  const threadQuery = {
    queryKey: resultsAiThreadKey(experimentId),
    queryFn: (): Promise<Thread> => client.get(`/results-ai/thread?experimentId=${experimentId}`),
  };

  const usage = useQuery<ResultsAiUsageResponse>({ ...usageQuery, enabled: available && open });
  const meter = usage.data?.credits ? meterView(usage.data.credits) : null;

  const { data: local = [] } = useQuery<ResultsTurn[]>({
    queryKey: localKey,
    queryFn: () => [],
    enabled: false,
    initialData: [],
    staleTime: Infinity,
  });
  const ownWorking = local.some((turn) => turn.phase === 'working');

  const thread = useQuery<Thread>({
    ...threadQuery,
    enabled: available && open,
    // While someone else's question runs, so their answer appears without a reload.
    // While a question runs that is not this browser's: a colleague's every COLLEAGUE_POLL_MS; the
    // person's own (another tab, or one still being saved after Stop) every second, since it frees
    // within moments and the input waits on it.
    refetchInterval: (query) => {
      const running = query.state.data?.running;
      if (!running || ownWorking) return false;
      return running.mine ? OWN_POLL_MS : COLLEAGUE_POLL_MS;
    },
  });
  const authors = thread.data?.authors ?? {};
  const stored = (thread.data?.turns ?? []).map((turn) => fromThreadTurn({ turn, authors }));
  const turns = mergeTurns(stored, local);

  // Through the cache's own updater, never a value captured at render: events arrive faster than renders.
  const updateLocal = (update: (current: ResultsTurn[]) => ResultsTurn[]) =>
    queryClient.setQueryData<ResultsTurn[]>(localKey, (current) => update(current ?? []));
  const updateTurn = (key: string, update: (turn: ResultsTurn) => ResultsTurn) =>
    updateLocal((current) => current.map((turn) => (turn.key === key ? update(turn) : turn)));

  // Leaving the experiment stops a running turn. Cleanup only, not data fetching.
  // `mountedRef` lets a question asked from the field, still reading the thread when the experiment
  // is left, not start a turn nobody could stop.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const running = thread.data?.running ?? null;
  /**
   * Why a question cannot be sent now because one is running on the experiment that is not this
   * browser's: "Anna is asking…", or the person's own from another tab (or one still being saved
   * after Stop), never in their own name. Null when none is.
   */
  const otherAsking =
    running && !ownWorking
      ? running.mine
        ? 'Your earlier question is still being answered…'
        : `${senderName(authors[String(running.senderId)])} is asking…`
      : null;

  const last = turns.at(-1);
  const phase = workingPhase(local.find((turn) => turn.phase === 'working'));
  const busy = ownWorking;
  const outOfCredits = !!meter?.exhausted;
  const canSend = acknowledged && !busy && !otherAsking && !outOfCredits && draft.trim().length > 0;

  /**
   * Reads the thread and the meter again after a turn, then drops this browser's copies of turns the
   * server now has: the stored one, with its sender, takes their place.
   */
  const refreshThread = async () => {
    void queryClient.invalidateQueries({ queryKey: resultsAiUsageKey(experimentId) });
    // The header's meter reads the same balance: a turn spent from it.
    void queryClient.invalidateQueries({ queryKey: AI_CREDIT_METER_KEY });
    await queryClient.invalidateQueries({ queryKey: resultsAiThreadKey(experimentId) });
    const known = new Set(
      (queryClient.getQueryData<Thread>(resultsAiThreadKey(experimentId))?.turns ?? []).map((turn) => turn.turnId),
    );
    updateLocal((current) => current.filter((turn) => !turn.turnId || !known.has(turn.turnId)));
  };

  const run = async (key: string, question: string) => {
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      await streamResultsChat({
        baseUrl: client.baseUrl,
        token: client.token,
        body: { experimentId, message: question, view },
        signal: controller.signal,
        onEvent: (event) => updateTurn(key, (turn) => applyEvent(turn, event)),
      });
      // A stream that ended without `done` (the connection dropped) still has to end the turn.
      updateTurn(key, (turn) =>
        turn.phase === 'working'
          ? {
              ...turn,
              phase: 'errored',
              error: { message: 'The answer was cut off. Please try again.', retryable: true },
            }
          : turn,
      );
    } catch (error) {
      if (controller.signal.aborted) {
        updateTurn(key, (turn) => ({ ...turn, phase: 'stopped' }));
      } else {
        const failure =
          error instanceof ResultsChatError
            ? { message: error.message, retryable: error.retryable, status: error.status }
            : { message: 'Something went wrong. Please try again.', retryable: true };
        updateTurn(key, (turn) => ({ ...turn, phase: 'errored', error: failure }));
        // Refused before it started (someone else's question is running, a rate limit): the
        // question was never asked, so it goes back into the box rather than being retyped.
        if (error instanceof ResultsChatError && (error.status === 409 || error.status === 429)) {
          setDraft((current) => (current.trim() ? current : question));
        }
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      // The stored turn replaces this browser's copy once the thread is read again.
      void refreshThread();
    }
  };

  // The ref, not only `busy`: two asks in one render (a double click) would both see busy false.
  // run() sets the ref before its first await, so the second one stops at it.
  const canAsk = () => !busy && !controllerRef.current && !otherAsking && acknowledged && !outOfCredits;

  /** Starts a turn without the checks: callers decide whether one may start. */
  const start = (text: string) => {
    const turn = newTurn({ key: crypto.randomUUID(), question: text });
    updateLocal((current) => [...current, turn]);
    setDraft((current) => (current.trim() === text ? '' : current));
    void run(turn.key, text);
  };

  const ask = (question: string) => {
    const text = question.trim();
    if (!text || !canAsk()) return;
    start(text);
  };

  const submit = () => ask(draft);

  /**
   * Ask from the header's field: the panel opens with the question asked. When it cannot be sent yet
   * (the first-use notice, a colleague asking, no credits) it waits in the panel's box, typed.
   */
  const submitFromField = async () => {
    const question = draft.trim();
    if (!question) return;
    setOpen(true);
    // The thread and the meter load with the panel, so with it closed until now neither is known:
    // read both first, or a colleague's question or an empty balance shows up as a refused turn
    // rather than as the reason the question waits. A failed read leaves the server to refuse.
    const [fresh, credits] = await Promise.all([
      queryClient.fetchQuery(threadQuery).catch(() => null),
      queryClient.fetchQuery(usageQuery).catch(() => null),
    ]);
    // Decided on what was just read, not on this render's copies, which may be a stale cache from the
    // last time the panel was open. Not at all once the experiment was left during the read.
    if (!mountedRef.current || controllerRef.current || !acknowledged) return;
    if (fresh?.running) return;
    if (credits?.credits && meterView(credits.credits).exhausted) return;
    start(question);
  };

  /**
   * A failed question asked again, as a new turn: this browser's failed copy makes way for it. Only
   * when it can be sent now, so a retry while someone else is asking never loses the question.
   */
  const retry = (key: string) => {
    const turn = turns.find((candidate) => candidate.key === key);
    if (!turn || !canAsk()) return;
    updateLocal((current) => current.filter((candidate) => candidate.key !== key));
    ask(turn.question);
  };

  const stop = () => controllerRef.current?.abort();

  /** A refusal (an account read-only until billing is sorted out, a network error) is said in the notice. */
  const acknowledge = async () => {
    setNoticeError(null);
    try {
      await client.post('/results-ai/acknowledge');
      queryClient.setQueryData<ResultsAiStatusResponse>(RESULTS_AI_STATUS_KEY, (current) =>
        current ? { ...current, acknowledged: true } : current,
      );
    } catch (error) {
      const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;
      setNoticeError(
        status === 402
          ? 'This account is read-only until its billing is sorted out, so the assistant cannot be used yet.'
          : 'That did not go through. Please try again.',
      );
    }
  };

  /** Enter sends, Shift+Enter adds a line, Esc stops a running turn and otherwise closes the panel. */
  const onInputKeyDown = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    // Composing a word with an IME: Safari reports it only as keyCode 229 (see AiAskField).
    if (ev.nativeEvent.isComposing || ev.keyCode === 229) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (busy) stop();
      else setOpen(false);
      return;
    }
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      if (canSend) submit();
    }
  };

  /** What the line beside Send says. */
  const statusLine = busy
    ? phase === 'writing'
      ? 'Writing…'
      : 'Thinking…'
    : (otherAsking ?? 'Enter to send, Shift+Enter for a new line');

  return {
    available,
    acknowledged,
    open,
    setOpen,
    toggle: () => setOpen(!open),
    isLoading: thread.isLoading,
    isError: thread.isError,
    noticeError,
    reload: () => void thread.refetch(),
    turns,
    last,
    draft,
    setDraft,
    phase,
    busy,
    otherAsking,
    canSend,
    outOfCredits,
    meter,
    billingUrl: usage.data?.billingUrl ?? null,
    statusLine,
    stoppedMessage: STOPPED_MESSAGE,
    viewLabel,
    ask,
    submit,
    submitFromField,
    retry,
    stop,
    acknowledge,
    onInputKeyDown,
  };
};

export type ResultsAiManager = ReturnType<typeof useResultsAi>;
